import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Extension,
  TrendingUp,
  Api,
  Security,
  DataObject,
  CloudSync,
  Integration,
  Inventory,
  LocalShipping,
  Apartment,
  BarChart,
  Settings
} from '@mui/icons-material';

const PageContainer = styled.div`
  padding-top: 0;
  min-height: 100vh;
`;

const Hero = styled.div`
  background: linear-gradient(
    150deg,
    #ff80b5 0%,
    #9089fc 20%,
    #ff80b5 40%,
    #40c9ff 60%,
    #a5fecb 80%,
    #9089fc 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 16rem 2rem 8rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    filter: blur(60px);
    opacity: 0.6;
  }

  h1 {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    position: relative;
    z-index: 2;
  }

  p {
    font-size: 1.25rem;
    max-width: 800px;
    margin: 0 auto;
    opacity: 0.9;
    line-height: 1.6;
    position: relative;
    z-index: 2;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const FeatureSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
  padding: 6rem 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &:nth-child(even) {
    direction: rtl;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    direction: ltr;
    padding: 4rem 0;
  }
`;

const TextContent = styled(motion.div)`
  direction: ltr;
  
  h3 {
    font-size: 1rem;
    color: #4f46e5;
    margin-bottom: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  h2 {
    font-size: 2.25rem;
    color: #1f2937;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    line-height: 1.2;
  }

  p {
    color: #4b5563;
    line-height: 1.8;
    margin-bottom: 2rem;
    font-size: 1.125rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 2rem 0;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.25rem;
    color: #4b5563;
    font-size: 1.125rem;

    svg {
      color: #4f46e5;
      margin-top: 0.25rem;
    }
  }
`;

const ImageContainer = styled(motion.div)`
  direction: ltr;
  
  img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  }
`;

const features = [
  {
    icon: <Api sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "API & Integrations",
    subtitle: "Connect Everything",
    description: "Extend ServTrackr's capabilities with our robust API and integration ecosystem. Connect with your favorite tools and build custom solutions.",
    benefits: [
      "RESTful API access",
      "Third-party integrations",
      "Custom webhook support",
      "Developer documentation"
    ],
    image: "/images/features/api-integration.jpg"
  },
  {
    icon: <Security sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Advanced Security",
    subtitle: "Enterprise-Grade Protection",
    description: "Keep your business data secure with enterprise-level security features. Multi-factor authentication, role-based access, and data encryption.",
    benefits: [
      "Multi-factor authentication",
      "Role-based access control",
      "Data encryption at rest",
      "Security audit logs"
    ],
    image: "/images/features/security.jpg"
  },
  {
    icon: <CloudSync sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Mobile Solutions",
    subtitle: "Work Anywhere",
    description: "Access your business from anywhere with our powerful mobile apps. Keep your team connected and productive on the go.",
    benefits: [
      "Native iOS and Android apps",
      "Offline mode support",
      "Real-time synchronization",
      "Push notifications"
    ],
    image: "/images/features/mobile-solutions.jpg"
  },
  {
    icon: <BarChart sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Custom Reporting",
    subtitle: "Business Intelligence",
    description: "Create custom reports and dashboards to track the metrics that matter to your business. Make data-driven decisions with powerful analytics.",
    benefits: [
      "Custom report builder",
      "Interactive dashboards",
      "Data export options",
      "Scheduled reports"
    ],
    image: "/images/features/custom-reporting.jpg"
  }
];

const AndMore = () => {
  return (
    <PageContainer>
      <Hero>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          And More Features
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Discover additional powerful features that make ServTrackr the complete solution for your field service business. From API integrations to mobile solutions, we've got you covered.
        </motion.p>
      </Hero>
      
      <Content>
        {features.map((feature, index) => (
          <FeatureSection key={feature.title}>
            <TextContent
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3>{feature.subtitle}</h3>
              <h2>{feature.icon} {feature.title}</h2>
              <p>{feature.description}</p>
              <ul>
                {feature.benefits.map((benefit, i) => (
                  <li key={i}>
                    <TrendingUp sx={{ fontSize: 20 }} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </TextContent>
            <ImageContainer
              initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img src={feature.image} alt={feature.title} />
            </ImageContainer>
          </FeatureSection>
        ))}
      </Content>
    </PageContainer>
  );
};

export default AndMore;
